import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Grid, Typography, Card, Box, Button, Alert,
  FormControl, Select, MenuItem
} from '@mui/material';
import { formatMatchDateTime } from '../../utils/matchTime';
import { isKnockoutGame } from '../../utils/tournamentCampaign';
import {
  getCompanyNow,
  getMatchDateTime,
  getMatchesForPredictor,
  getPredictionWindowState,
} from '../../utils/tournamentPredictionWindows';

/** Format remaining time as "Xd Xh Xm Xs" or null when past deadline */
function formatCountdown(deadline, companyTimezone = 'UTC') {
  const now = getCompanyNow(companyTimezone);
  if (deadline.isBefore(now) || deadline.isSame(now)) {
    return null;
  }
  const diff = deadline.diff(now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  parts.push(`${seconds}s`);
  return parts.join(' ');
}

function useCountdown(target, companyTimezone = 'UTC') {
  const [countdown, setCountdown] = useState(
    target ? formatCountdown(target, companyTimezone) : null
  );
  useEffect(() => {
    if (!target) {
      setCountdown(null);
      return undefined;
    }
    const tick = () => setCountdown(formatCountdown(target, companyTimezone));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target?.valueOf(), companyTimezone]);
  return countdown;
}

function copyPredictionEntry(prev) {
  if (prev == null || typeof prev !== 'object') return {};
  const out = {};
  if (typeof prev.score === 'string') out.score = prev.score;
  if (prev.winner === 'team1' || prev.winner === 'team2') out.winner = prev.winner;
  return out;
}

function buildMergedGamesPayload(moduleGamesLen, savedPredictions, updateIndex, updateEntry) {
  const out = [];
  for (let i = 0; i < moduleGamesLen; i++) {
    if (i === updateIndex) {
      out.push(updateEntry);
    } else {
      out.push(copyPredictionEntry(savedPredictions[i]));
    }
  }
  return out;
}

function PredictionCountdownBox({ countdownPhase, countdown, knockout = false }) {
  const noun = knockout ? 'prediction' : 'score prediction';

  if (countdownPhase === 'start') {
    return (
      <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'action.hover', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          Time to start predictions: <strong>{countdown}</strong>
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Predictions will open at this time.
        </Typography>
      </Box>
    );
  }

  if (countdownPhase === 'edit' && countdown !== null) {
    return (
      <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'action.hover', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          Time left to edit prediction: <strong>{countdown}</strong>
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {`You will not be able to change the ${noun} after this time.`}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'action.hover', borderRadius: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        Editing closed
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {`You can no longer change the ${noun}.`}
      </Typography>
    </Box>
  );
}

async function persistPrediction({
  goal,
  canPredict,
  gamesPayload,
  apiUrl,
  selectedCampaignDetail,
  loadCampaignGoals,
  setEditingGameIndex,
}) {
  if (!goal || !canPredict) return false;
  try {
    const token = localStorage.getItem('token');
    const base = (apiUrl || '').replace(/\/$/, '');
    const res = await fetch(`${base}/api/goals/${goal.id}/predictions`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ games: gamesPayload }),
    });
    if (res.ok) {
      await loadCampaignGoals(selectedCampaignDetail.id);
      setEditingGameIndex(null);
      return true;
    }
  } catch (e) {
    console.error(e);
  }
  return false;
}

function KnockoutMatchPredictionCard({
  headingPrefix,
  game,
  gameIdx,
  goal,
  savedPredictions,
  editingGameIndex,
  setEditingGameIndex,
  selectedCampaignDetail,
  moduleGamesLen,
  apiUrl,
  loadCampaignGoals,
  companyTimezone = 'UTC',
}) {
  const matchDateTime = getMatchDateTime(game, companyTimezone);
  const companyNow = getCompanyNow(companyTimezone);
  const windowState = getPredictionWindowState(matchDateTime, companyNow);
  const { canPredict, countdownPhase, countdownTarget } = windowState;
  const countdown = useCountdown(
    countdownPhase === 'start' || countdownPhase === 'edit' ? countdownTarget : null,
    companyTimezone
  );

  const savedPred = savedPredictions[gameIdx];
  const savedWinner = savedPred?.winner;
  const hasPrediction = savedWinner === 'team1' || savedWinner === 'team2';
  const canEdit = hasPrediction && canPredict;
  const [pickedWinner, setPickedWinner] = useState(savedWinner || '');

  useEffect(() => {
    setPickedWinner(savedPred?.winner || '');
  }, [savedPred?.winner, gameIdx]);

  const disabledInputs =
    !canPredict || (hasPrediction && editingGameIndex !== gameIdx);

  const savePrediction = useCallback(async () => {
    if (!pickedWinner) return;
    await persistPrediction({
      goal,
      canPredict,
      gamesPayload: buildMergedGamesPayload(
        moduleGamesLen,
        savedPredictions,
        gameIdx,
        { winner: pickedWinner }
      ),
      apiUrl,
      selectedCampaignDetail,
      loadCampaignGoals,
      setEditingGameIndex,
    });
  }, [
    goal,
    canPredict,
    pickedWinner,
    gameIdx,
    savedPredictions,
    selectedCampaignDetail,
    loadCampaignGoals,
    setEditingGameIndex,
    moduleGamesLen,
    apiUrl,
  ]);

  const formatted = formatMatchDateTime(game.date, game.time, companyTimezone);
  const buttonDisabled =
    !canPredict || !pickedWinner || (hasPrediction && editingGameIndex !== gameIdx && !canEdit);

  return (
    <Card sx={{ p: 3, textAlign: 'center', mb: 2 }}>
      <Typography variant="h6" gutterBottom color="primary">
        {headingPrefix}
        {formatted.date} {formatted.time}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {game.stage} · {game.venue}
      </Typography>

      <PredictionCountdownBox countdownPhase={countdownPhase} countdown={countdown} knockout />

      <Typography variant="body1" sx={{ my: 3, fontWeight: 500 }}>
        Pick the winning team
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, mb: 3 }}>
        {(['team1', 'team2']).map((side) => {
          const label = side === 'team1' ? game.team1 : game.team2;
          const flag = side === 'team1' ? game.team1_flag : game.team2_flag;
          const selected = pickedWinner === side;
          return (
            <Button
              key={side}
              variant={selected ? 'contained' : 'outlined'}
              size="large"
              disabled={disabledInputs}
              onClick={() => setPickedWinner(side)}
              sx={{ minWidth: 180, py: 2 }}
            >
              <span style={{ marginRight: 8, fontSize: 24 }}>{flag || '🇺🇳'}</span>
              {label}
            </Button>
          );
        })}
      </Box>

      <Button
        variant="contained"
        color={hasPrediction && editingGameIndex !== gameIdx ? 'secondary' : 'primary'}
        size="large"
        disabled={buttonDisabled}
        onClick={async () => {
          if (hasPrediction && editingGameIndex !== gameIdx) {
            setEditingGameIndex(gameIdx);
            return;
          }
          await savePrediction();
        }}
      >
        {(() => {
          if (!hasPrediction) return 'CONFIRM';
          if (editingGameIndex === gameIdx) return 'SAVE CHANGES';
          if (canEdit) return 'EDIT PREDICTION';
          return 'PREDICTION SAVED';
        })()}
      </Button>
    </Card>
  );
}

function MatchPredictionCard({
  headingPrefix,
  game,
  gameIdx,
  goal,
  savedPredictions,
  editingGameIndex,
  setEditingGameIndex,
  selectedCampaignDetail,
  moduleGamesLen,
  apiUrl,
  loadCampaignGoals,
  companyTimezone = 'UTC',
}) {
  const matchDateTime = getMatchDateTime(game, companyTimezone);
  const companyNow = getCompanyNow(companyTimezone);
  const windowState = getPredictionWindowState(matchDateTime, companyNow);
  const { canPredict, countdownPhase, countdownTarget } = windowState;
  const countdown = useCountdown(
    countdownPhase === 'start' || countdownPhase === 'edit' ? countdownTarget : null,
    companyTimezone
  );

  const savedPred = savedPredictions[gameIdx];
  const savedScore = savedPred?.score;
  const scoreParts = (savedScore || ':').split(':');
  const savedScore1 = scoreParts[0] ?? '0';
  const savedScore2 = scoreParts[1] ?? '0';

  const hasPrediction =
    Boolean(savedScore && savedScore.includes(':') && savedScore !== ':');

  const canEdit = hasPrediction && canPredict;

  const [s1, setS1] = useState(savedScore1 || '0');
  const [s2, setS2] = useState(savedScore2 || '0');

  useEffect(() => {
    const [a, b] = (savedPred?.score || ':').split(':');
    setS1(a || '0');
    setS2(b || '0');
  }, [savedPred?.score, gameIdx]);

  const disabledInputs =
    !canPredict || (hasPrediction && editingGameIndex !== gameIdx);

  const savePrediction = useCallback(async () => {
    const newScore = `${s1}:${s2}`;
    await persistPrediction({
      goal,
      canPredict,
      gamesPayload: buildMergedGamesPayload(
        moduleGamesLen,
        savedPredictions,
        gameIdx,
        { score: newScore }
      ),
      apiUrl,
      selectedCampaignDetail,
      loadCampaignGoals,
      setEditingGameIndex,
    });
  }, [
    goal,
    canPredict,
    s1,
    s2,
    gameIdx,
    savedPredictions,
    selectedCampaignDetail,
    loadCampaignGoals,
    setEditingGameIndex,
    moduleGamesLen,
    apiUrl,
  ]);

  const formatted = formatMatchDateTime(game.date, game.time, companyTimezone);

  const buttonDisabled =
    !canPredict || (hasPrediction && editingGameIndex !== gameIdx && !canEdit);

  const flagSx = { fontSize: { xs: 24, md: 32 }, flexShrink: 0, lineHeight: 1 };

  const team1Block = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, md: 2 },
        flex: { xs: 1, md: 'none' },
        minWidth: 0,
        justifyContent: { xs: 'flex-end', md: 'center' },
      }}
    >
      <Typography
        fontWeight="bold"
        sx={{
          typography: { xs: 'subtitle1', md: 'h5' },
          textAlign: { xs: 'right', md: 'inherit' },
          wordBreak: 'break-word',
        }}
      >
        {game.team1}
      </Typography>
      <Box component="span" sx={flagSx}>{game.team1_flag || '🇺🇳'}</Box>
    </Box>
  );

  const team2Block = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, md: 2 },
        flex: { xs: 1, md: 'none' },
        minWidth: 0,
        justifyContent: { xs: 'flex-start', md: 'center' },
      }}
    >
      <Box component="span" sx={flagSx}>{game.team2_flag || '🇺🇳'}</Box>
      <Typography
        fontWeight="bold"
        sx={{
          typography: { xs: 'subtitle1', md: 'h5' },
          textAlign: { xs: 'left', md: 'inherit' },
          wordBreak: 'break-word',
        }}
      >
        {game.team2}
      </Typography>
    </Box>
  );

  const scoreControls = (
    <>
      <FormControl sx={{ minWidth: { xs: 64, md: 80 } }}>
        <Select
          value={s1}
          onChange={(e) => setS1(e.target.value)}
          disabled={disabledInputs}
          size="small"
        >
          {[...Array(10).keys()].map((n) => (
            <MenuItem key={n} value={n.toString()}>
              {n}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography fontWeight="bold" sx={{ typography: { xs: 'subtitle1', md: 'h5' } }}>:</Typography>

      <FormControl sx={{ minWidth: { xs: 64, md: 80 } }}>
        <Select
          value={s2}
          onChange={(e) => setS2(e.target.value)}
          disabled={disabledInputs}
          size="small"
        >
          {[...Array(10).keys()].map((n) => (
            <MenuItem key={n} value={n.toString()}>
              {n}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );

  return (
    <Card
      sx={{ p: { xs: 2, md: 3 }, textAlign: 'center', mb: 2 }}
      key={`predictor-${goal?.id || 'none'}-${gameIdx}-${savedScore || ''}`}
    >
      <Typography variant="h6" gutterBottom color="primary">
        {headingPrefix}
        {formatted.date} {formatted.time}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {game.stage} · {game.venue}
      </Typography>

      <PredictionCountdownBox countdownPhase={countdownPhase} countdown={countdown} />

      <Box sx={{ my: 4, width: '100%' }}>
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            width: '100%',
          }}
        >
          {team1Block}
          {team2Block}
        </Box>

        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 1, md: 2 },
          }}
        >
          {scoreControls}
        </Box>

        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          {team1Block}
          {scoreControls}
          {team2Block}
        </Box>
      </Box>

      <Button
        variant="contained"
        color={hasPrediction && editingGameIndex !== gameIdx ? 'secondary' : 'primary'}
        size="large"
        disabled={buttonDisabled}
        onClick={async () => {
          if (hasPrediction && editingGameIndex !== gameIdx) {
            setEditingGameIndex(gameIdx);
            return;
          }
          await savePrediction();
        }}
      >
        {(() => {
          if (!hasPrediction) return 'CONFIRM';
          if (editingGameIndex === gameIdx) return 'SAVE CHANGES';
          if (canEdit) return 'EDIT PREDICTION';
          return 'PREDICTION SAVED';
        })()}
      </Button>
    </Card>
  );
}

/**
 * Match Predictor Section – Tournament extra module UI component.
 */
const MatchPredictorSection = ({
  moduleConfig,
  campaignGoals,
  selectedCampaignDetail,
  apiUrl,
  loadCampaignGoals,
  companyTimezone = 'UTC',
}) => {
  const games = moduleConfig.games || [];
  const [editingGameIndex, setEditingGameIndex] = useState(null);

  useEffect(() => {
    setEditingGameIndex(null);
  }, [selectedCampaignDetail?.id]);

  const goal = campaignGoals?.length ? campaignGoals[0] : null;
  const savedPredictions = goal?.extra?.games || [];

  const nextMatches = useMemo(
    () => getMatchesForPredictor(games, undefined, companyTimezone),
    [games, companyTimezone]
  );

  if (nextMatches.length === 0) {
    return (
      <Grid item xs={12} sx={{ pl: 0, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          MATCH PREDICTOR
        </Typography>
        <Alert severity="info">No upcoming matches</Alert>
      </Grid>
    );
  }

  return (
    <Grid item xs={12} sx={{ pl: 0, mb: 4 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        MATCH PREDICTOR
      </Typography>
      {nextMatches.map(({ game, idx }, listPos) => {
        const cardProps = {
          headingPrefix: listPos === 0 ? 'NEXT MATCH ' : 'UPCOMING · ',
          game,
          gameIdx: idx,
          goal,
          savedPredictions,
          editingGameIndex,
          setEditingGameIndex,
          selectedCampaignDetail,
          moduleGamesLen: games.length,
          apiUrl,
          loadCampaignGoals,
          companyTimezone,
        };
        return isKnockoutGame(game) ? (
          <KnockoutMatchPredictionCard key={idx} {...cardProps} />
        ) : (
          <MatchPredictionCard key={idx} {...cardProps} />
        );
      })}
    </Grid>
  );
};

export default MatchPredictorSection;
